import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, AlertCircle, CalendarClock } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/contact-linked2marketing/30min";

const SECTORS = [
  "Yacht charter",
  "Luxury travel",
  "Boutique hospitality",
  "Tourism",
  "Private experiences",
  "Concierge services",
  "Other premium service business",
];

const IMPROVEMENTS = [
  "Website and digital presence",
  "CRM / enquiry management",
  "Booking or operational workflows",
  "Guest/customer communication",
  "Marketing automation",
  "Reporting and visibility",
  "AI-assisted workflows",
  "Not sure yet",
];

const PRIORITIES = [
  "Exploring options",
  "Needs attention soon",
  "Active project",
  "Urgent operational issue",
];

const EMPTY = {
  name: "",
  business_name: "",
  email: "",
  website_url: "",
  sector: "",
  improvements: [],
  current_systems: "",
  priority: "",
  message: "",
  // Honeypots — must remain empty when a human submits the form.
  website_url_confirm: "",
  company_extra: "",
};

/**
 * Premium inline enquiry form. Submits to the existing /api/strategy-call
 * endpoint by mapping the new fields onto the established schema, so the
 * backend MongoDB + Gmail SMTP pipeline keeps working unchanged.
 */
export default function EnquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  // Track when the form first mounts so we can ignore sub-second submissions
  // (almost always bots auto-filling and posting immediately).
  const mountedAtRef = React.useRef(Date.now());
  const submitting = status.state === "submitting";
  const success = status.state === "success";

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
  const toggleImprovement = (v) =>
    setForm((f) => ({
      ...f,
      improvements: f.improvements.includes(v)
        ? f.improvements.filter((x) => x !== v)
        : [...f.improvements, v],
    }));

  const composeChallenge = (f) => {
    const parts = [];
    if (f.current_systems?.trim())
      parts.push(`Current systems / tools:\n${f.current_systems.trim()}`);
    if (f.message?.trim()) parts.push(`Message:\n${f.message.trim()}`);
    return parts.join("\n\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus({
        state: "error",
        message: "Please add your name and email so we can respond.",
      });
      return;
    }
    setStatus({ state: "submitting", message: "" });

    const payload = {
      name: form.name,
      business_name: form.business_name || null,
      email: form.email,
      website_url: form.website_url || null,
      industry: form.sector || null,
      needs: form.improvements || [],
      challenge: composeChallenge(form) || null,
      preferred_contact: form.priority || null,
      // Anti-spam: honeypot fields. Humans never see / fill these — bots do.
      website_url_confirm: form.website_url_confirm || "",
      company_extra: form.company_extra || "",
    };

    // Anti-spam: silently no-op for impossibly fast submissions (likely bots).
    // Real humans take well over 2 seconds to fill an enquiry form. Mimic the
    // success state so spam tooling can't differentiate.
    const elapsed = Date.now() - mountedAtRef.current;
    if (elapsed < 2000) {
      setStatus({
        state: "success",
        message:
          "Thank you — your enquiry has been received. We'll review the details and respond with a considered next step.",
      });
      setForm(EMPTY);
      return;
    }

    try {
      await axios.post(`${API}/strategy-call`, payload);
      setStatus({
        state: "success",
        message:
          "Thank you — your enquiry has been received. We'll review the details and respond with a considered next step.",
      });
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? "Please check the form fields."
        : detail || "Something went wrong. Please try again.";
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36 scroll-mt-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        {/* Intro column */}
        <div className="lg:col-span-5">
          <span className="eyebrow text-gold">Start a conversation</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            A considered conversation, not a sales sequence.
          </h2>
          <p className="mt-6 text-[16.5px] leading-[1.75] text-mute-200 max-w-lg">
            Tell us a little about your business, your current digital setup, and where the customer
            journey could feel calmer, clearer, or more connected. We'll respond personally — usually
            within one business day.
          </p>
          <div className="hairline my-8" />
          <div className="flex flex-col gap-4 max-w-md">
            <div className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-mute-200">
              <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-gold flex-none" />
              <span>Send a brief enquiry — for a written, considered response.</span>
            </div>
            <div className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-mute-200">
              <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-gold/60 flex-none" />
              <span>
                Or, if you prefer, book a quiet 30-minute consultation with us directly.
              </span>
            </div>
          </div>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Book a 30-minute consultation (opens Calendly in a new tab)"
            data-testid="enq-calendly-cta"
            className="group mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.02] px-5 py-2.5 text-[13.5px] tracking-[0.02em] text-mute-100 hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05] transition-colors"
          >
            <CalendarClock className="w-4 h-4 text-gold" />
            Book a 30-min consultation
            <ArrowUpRight className="w-3.5 h-3.5 opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Form column */}
        <div className="lg:col-span-7">
          {success ? (
            <SuccessPanel onReset={() => setStatus({ state: "idle", message: "" })} />
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="enquiry-form"
              className="relative rounded-3xl glass p-7 sm:p-10 overflow-hidden"
            >
              {/*
                Honeypot fields — visually hidden, off-screen, not focusable,
                not announced. Real humans never interact with them; bots will
                eagerly fill them and the backend silently discards the
                submission.
              */}
              <div
                aria-hidden="true"
                className="absolute -left-[9999px] top-[-9999px] h-0 w-0 overflow-hidden"
                style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
              >
                <label htmlFor="website_url_confirm">
                  Do not fill this field
                </label>
                <input
                  id="website_url_confirm"
                  name="website_url_confirm"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website_url_confirm}
                  onChange={update("website_url_confirm")}
                />
                <label htmlFor="company_extra">
                  Do not fill this field
                </label>
                <input
                  id="company_extra"
                  name="company_extra"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_extra}
                  onChange={update("company_extra")}
                />
              </div>

              <div
                className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(199,166,106,0.12), transparent 70%)",
                }}
              />

              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <Input
                    value={form.name}
                    onChange={update("name")}
                    data-testid="enq-name"
                    placeholder="Your full name"
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300 focus-visible:ring-gold/40 focus-visible:border-gold/40"
                  />
                </Field>
                <Field label="Business / brand name">
                  <Input
                    value={form.business_name}
                    onChange={update("business_name")}
                    data-testid="enq-business"
                    placeholder="Company or brand"
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300"
                  />
                </Field>
                <Field label="Email address" required>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="enq-email"
                    placeholder="you@business.com"
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300"
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={form.website_url}
                    onChange={update("website_url")}
                    data-testid="enq-website"
                    placeholder="https://"
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300"
                  />
                </Field>
                <Field label="Sector">
                  <Select
                    value={form.sector}
                    onValueChange={(v) => setForm({ ...form, sector: v })}
                  >
                    <SelectTrigger
                      data-testid="enq-sector"
                      className="bg-white/[0.025] border-white/10 text-mute-100"
                    >
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent className="bg-ink-600 border-white/10 text-mute-100">
                      {SECTORS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`enq-sector-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Approximate priority">
                  <Select
                    value={form.priority}
                    onValueChange={(v) => setForm({ ...form, priority: v })}
                  >
                    <SelectTrigger
                      data-testid="enq-priority"
                      className="bg-white/[0.025] border-white/10 text-mute-100"
                    >
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent className="bg-ink-600 border-white/10 text-mute-100">
                      {PRIORITIES.map((p) => (
                        <SelectItem
                          key={p}
                          value={p}
                          data-testid={`enq-priority-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="relative mt-7">
                <Label className="text-mute-100 text-sm">What are you looking to improve?</Label>
                <p className="mt-1 text-[12.5px] text-mute-300">
                  Select any that apply.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {IMPROVEMENTS.map((opt) => {
                    const active = form.improvements.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => toggleImprovement(opt)}
                        data-testid={`enq-improve-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`rounded-full px-3.5 py-1.5 text-xs sm:text-sm border transition-all ${
                          active
                            ? "bg-gold/12 border-gold/50 text-gold"
                            : "bg-white/[0.025] border-white/10 text-mute-200 hover:border-white/25"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="relative mt-7 grid grid-cols-1 gap-5">
                <Field label="Current systems or tools">
                  <Input
                    value={form.current_systems}
                    onChange={update("current_systems")}
                    data-testid="enq-current-systems"
                    placeholder="e.g. WordPress site, HubSpot, manual spreadsheets, Mailchimp…"
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300"
                  />
                </Field>
                <Field label="Message">
                  <Textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="enq-message"
                    placeholder="Briefly describe what feels disconnected, manual, unclear, or ready to improve."
                    className="bg-white/[0.025] border-white/10 text-mute-100 placeholder:text-mute-300 resize-none"
                  />
                </Field>
              </div>

              <div className="relative mt-8 flex items-center justify-between gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enq-submit"
                  className="btn-primary disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send enquiry"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <span className="text-[12px] text-mute-300 hidden sm:inline">
                  Replies usually within one business day.
                </span>
              </div>
              <p
                className="relative mt-4 text-[12px] leading-[1.6] text-mute-300"
                data-testid="enq-privacy-notice"
              >
                By sending this enquiry, you agree to our{" "}
                <a
                  href="/privacy"
                  className="text-mute-200 underline-offset-4 hover:text-gold hover:underline"
                >
                  Privacy Policy
                </a>
                . We use your details only to respond to your enquiry — never for marketing lists or shared with third parties.
              </p>

              {status.state === "error" && (
                <div
                  data-testid="enq-error"
                  className="relative mt-6 rounded-xl border border-red-400/30 bg-red-500/[0.07] p-4 flex items-start gap-3 text-red-300"
                >
                  <AlertCircle className="w-5 h-5 flex-none" />
                  <p className="text-sm">{status.message}</p>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-mute-100 text-sm">
        {label} {required && <span className="text-gold">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SuccessPanel({ onReset }) {
  return (
    <div
      data-testid="enq-success"
      className="relative rounded-3xl glass-strong p-10 sm:p-14 overflow-hidden glow-gold"
    >
      <div
        className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.18), transparent 70%)",
        }}
      />
      <div className="relative">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/30 bg-gold/[0.08] text-gold">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <h3 className="mt-6 font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight text-mute-100">
          Thank you — your enquiry has been received.
        </h3>
        <p className="mt-5 text-[15.5px] leading-[1.7] text-mute-200 max-w-xl">
          We'll review the details and respond with a considered next step.
        </p>
        <button
          type="button"
          onClick={onReset}
          data-testid="enq-reset"
          className="mt-8 text-[13px] text-mute-300 hover:text-gold transition-colors"
        >
          Send another enquiry →
        </button>
      </div>
    </div>
  );
}
